import React, { useEffect, useState } from "react";
import { Send, MailOpen, MousePointerClick, Clock, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { api } from "@/contexts/AuthContext";

const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const Stat = ({ icon: I, label, value, sub, testid }) => (
  <div className="border border-[#E9ECEF] rounded-sm bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]"><I size={12} className="text-[#D4AF37]" /> {label}</div>
    <div className="text-3xl font-black text-[#0B132B] mt-1">{value}</div>
    {sub && <div className="text-[11px] text-[#6C757D] mt-0.5">{sub}</div>}
  </div>
);

export function StatsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/email-campaigns/stats").then(({ data }) => setS(data)).catch(() => setS({ error: true })); }, []);
  if (!s) return <Card className="rounded-sm border-[#E9ECEF] p-5 text-sm text-[#6C757D]">Loading…</Card>;
  if (s.error) return <Card className="rounded-sm border-[#E9ECEF] p-5 text-sm text-red-700">Could not load statistics.</Card>;
  const max = Math.max(1, ...s.months.map((m) => m.sends));
  return (
    <div className="space-y-4" data-testid="ec-stats">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat icon={Send} label="Campaigns sent" value={s.campaigns} sub={`${s.scheduled} scheduled`} testid="ec-stat-campaigns" />
        <Stat icon={Send} label="Emails delivered" value={s.sends} sub={s.failed ? `${s.failed} failed` : "no failures"} testid="ec-stat-sends" />
        <Stat icon={MailOpen} label="Open rate" value={pct(s.open_rate)} sub={`${s.opened} of ${s.tracked} tracked`} testid="ec-stat-open" />
        <Stat icon={MousePointerClick} label="Click rate" value={pct(s.click_rate)} sub={`${s.clicked} clicked`} testid="ec-stat-click" />
        <Stat icon={TrendingUp} label="Click-to-open" value={pct(s.ctor)} sub="clicks ÷ opens" testid="ec-stat-ctor" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
        <Card className="rounded-sm border-[#E9ECEF] p-5" data-testid="ec-stats-months">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-3">Sends by month</div>
          {s.months.length === 0 ? <div className="text-sm italic text-[#6C757D]">No sent campaigns yet.</div> : (
            <div className="flex items-end gap-2 h-44">
              {s.months.map((m) => (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full" title={`${m.sends} sent · ${m.opened} opened · ${m.clicked} clicked`}>
                  <div className="text-[10px] font-mono text-[#0B132B] mb-1">{m.sends}</div>
                  <div className="w-full bg-[#0B132B] rounded-t-sm relative" style={{ height: `${Math.max(4, (m.sends / max) * 100)}%` }}>
                    <div className="absolute bottom-0 left-0 right-0 bg-[#D4AF37]" style={{ height: m.tracked ? `${(m.opened / m.tracked) * 100}%` : 0 }} />
                  </div>
                  <div className="text-[9px] text-[#6C757D] mt-1">{m.month.slice(2).replace("-", "/")}</div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[10px] text-[#6C757D] mt-2">Navy = emails sent · Gold = share opened</div>
        </Card>
        <Card className="rounded-sm border-[#E9ECEF] p-5" data-testid="ec-stats-top">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-3">Top campaigns by opens</div>
          {s.top.length === 0 ? <div className="text-sm italic text-[#6C757D]">Nothing yet.</div> : s.top.map((t) => (
            <div key={t.id} className="py-2 border-b border-[#F1F3F5] last:border-0">
              <div className="text-sm font-semibold text-[#0B132B] truncate">{t.subject}</div>
              <div className="text-[11px] text-[#6C757D] flex gap-3"><span>{t.sent} sent</span><span className="text-green-700 font-semibold">{t.opened} opened{t.tracked ? ` (${Math.round((t.opened / t.tracked) * 100)}%)` : ""}</span><span>{t.clicked} clicked</span><span className="ml-auto flex items-center gap-1"><Clock size={9} /> {t.sent_at ? new Date(t.sent_at).toLocaleDateString() : ""}</span></div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
